import {UpsellDialogEntryPoint} from '@Libraries/upsell-dialog.library';
import type {EmailCampaign, SocialShareThumbnailMeta} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {
  EmailCampaignPublishingOptions,
  EmailCampaignPublishOptionsSublow,
  EmailCampaignSocialMediaShareOptions,
} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {
  setEmailPublishingSubflow,
  setSocialMediaSubflow,
  setSocialShareThumbnailMeta,
  setWebpagePublishingFlow,
} from '@Components/email-marketing-campaigns/email-campaign-publish-options.reducer';
import {getEmailMarketingCampaignById, openCampaignInWizard, trackGA4NumberOfSectionsInEmailCampaign} from '@Libraries/email-campaigns-library';
import type {ReactElement} from 'react';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import type {PublishingOptions} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import {PublishingOptionsList} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import {ThumbNailType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {shareItemOnFacebook, shareItemOnPinterest, shareItemOnX} from '@Libraries/share-social-platform-library';

export const fetchAndSetSocialShareThumbnails = (campaignId: string): void => {
  window.PMW.writeLocal(`emailmarketing/getSocialPlatformShareThumbnails`, {campaignId})
    .then((data: any) => {
      const urls = data as SocialShareThumbnailMeta;
      window.PMW.redux.store.dispatch(setSocialShareThumbnailMeta(urls));
    })
    .catch((e) => {
      console.log('Error in fetching OG image thumbnails: ', e);
    });
};

export const onClickMenuOption = async (optionType: EmailCampaignPublishingOptions, campaignId: string): Promise<void> => {
  if (optionType === EmailCampaignPublishingOptions.SOCIAL_MEDIA) {
    window.PMW.redux.store.dispatch(setSocialMediaSubflow());
    return;
  }

  if (optionType === EmailCampaignPublishingOptions.EMAIL) {
    openCampaignInWizard(`emailmarketing/publish/campaign/${campaignId}`, UpsellDialogEntryPoint.EMAIL_CAMPAIGN_PUBLISH_OPTIONS);
    window.PMW.redux.store.dispatch(setEmailPublishingSubflow());
    return;
  }

  if (optionType === EmailCampaignPublishingOptions.WEBPAGE) {
    window.PMW.redux.store.dispatch(setWebpagePublishingFlow());
  }
};

export const areSocialThumbnailsLoaded = (): boolean => {
  const socialThumbnailMeta = window.PMW.redux.store.getState().emailCampaignPublishOptions.socialShareThumbnails;
  return !!socialThumbnailMeta;
};

export const getLoader = (loaderWrapperClasses: string): ReactElement<any> => {
  return (
    <div className={loaderWrapperClasses}>
      <div style={{height: '32px', width: '32px'}}>
        <CircularProgressLoader size={LOADER_SIZE.MEDIUM} />
      </div>
    </div>
  );
};

export const getPublishingOptions = (publishingSubflow: EmailCampaignPublishOptionsSublow, campaign: EmailCampaign): PublishingOptions[] => {
  if (publishingSubflow === EmailCampaignPublishOptionsSublow.MAIN_OPTIONS) {
    return getAllDefaultPublishingOptions(campaign);
  }

  return getSharePlatformOptions();
};

const getEmailCampaignId = (): string => {
  return window.PMW.redux.store.getState().emailCampaignPublishOptions.campaignId;
};

const getEmailCampaignUrls = (): SocialShareThumbnailMeta | null => {
  return window.PMW.redux.store.getState().emailCampaignPublishOptions.socialShareThumbnails;
};

const shareOnPinterest = (pinterestLink: string, campaignName: string): void => {
  const urls = getEmailCampaignUrls();

  if (!urls) {
    return;
  }

  const pinterestUrl = urls.pinterestThumb;

  shareItemOnPinterest(pinterestLink, campaignName, pinterestUrl);
};

const shareOnFacebook = (facebookLink: string, campaignName: string): void => {
  shareItemOnFacebook(facebookLink, campaignName);
};

const shareOnX = (xLink: string, campaignName: string): void => {
  shareItemOnX(xLink, campaignName);
};

export const onClickShareOptions = async (shareOptionType: EmailCampaignSocialMediaShareOptions): Promise<void> => {
  const campaignId = getEmailCampaignId();
  const link = getShareableUrlForEmailCampaign(campaignId);
  const campaign = await getEmailMarketingCampaignById(campaignId);

  if (campaign === undefined) {
    return;
  }

  if (shareOptionType === EmailCampaignSocialMediaShareOptions.FACEBOOK) {
    shareOnFacebook(link, campaign.name);
    return;
  }

  if (shareOptionType === EmailCampaignSocialMediaShareOptions.PINTEREST) {
    shareOnPinterest(link, campaign.name);
    return;
  }

  if (shareOptionType === EmailCampaignSocialMediaShareOptions.X) {
    shareOnX(link, campaign.name);
  }
};

const getShareableUrlForEmailCampaign = (campaignId: string): string => {
  return window.PMW.util.site_url(`emailmarketing/share/${campaignId}`);
};

export const getSharePlatformOptions = (): PublishingOptions[] => {
  return [
    {
      id: EmailCampaignSocialMediaShareOptions.PINTEREST,
      imgSrc: 'publishing-social-pinterest.png',
      iconName: '',
      heading: window.i18next.t('social_media_share_pinterest'),
      subheading: window.i18next.t('social_media_share_pinterest_desc'),
      thumbNailType: ThumbNailType.IMAGE,
      onClick: (): void => {
        void onClickShareOptions(EmailCampaignSocialMediaShareOptions.PINTEREST);
      },
      customThumbnailClasses: 'bg-neutral-3',
    },
    {
      id: EmailCampaignSocialMediaShareOptions.FACEBOOK,
      iconName: '',
      heading: window.i18next.t('social_media_share_facebook'),
      subheading: window.i18next.t('social_media_share_facebook_desc'),
      thumbNailType: ThumbNailType.IMAGE,
      imgSrc: 'publishing-social-facebook.png',
      onClick: (): void => {
        void onClickShareOptions(EmailCampaignSocialMediaShareOptions.FACEBOOK);
      },
      customThumbnailClasses: 'bg-neutral-3',
    },
    {
      id: EmailCampaignSocialMediaShareOptions.X,
      iconName: '',
      heading: window.i18next.t('social_media_share_x'),
      imgSrc: 'publishing-social-x.png',
      subheading: window.i18next.t('social_media_share_x_desc'),
      thumbNailType: ThumbNailType.IMAGE,
      onClick: (): void => {
        void onClickShareOptions(EmailCampaignSocialMediaShareOptions.X);
      },
      customThumbnailClasses: 'bg-neutral-3',
    },
  ];
};

const getAllDefaultPublishingOptions = (campaign: EmailCampaign): PublishingOptions[] => {
  const campaignId = getEmailCampaignId();
  const isCampaignAlreadyPublished = !campaign.isEditable;

  const publishingOptions = [
    {
      id: PublishingOptionsList.SOCIAL_MEDIA,
      iconName: 'icon-social-media',
      heading: window.i18next.t('pmwjs_social_media'),
      subheading: window.i18next.t('pmwjs_email_campaign_post_social_media'),
      thumbNailType: ThumbNailType.ICON,
      onClick: (): void => {
        trackGA4NumberOfSectionsInEmailCampaign(campaignId);
        void onClickMenuOption(EmailCampaignPublishingOptions.SOCIAL_MEDIA, campaignId);
      },
      customThumbnailClasses: 'bg-primary-10 content-primary',
    },
    {
      id: PublishingOptionsList.WEB_LINK,
      iconName: 'icon-link',
      heading: window.i18next.t('pmwjs_publish_weblink'),
      subheading: window.i18next.t('pmwjs_email_campaign_publish_as_webpage'),
      thumbNailType: ThumbNailType.ICON,
      onClick: (): void => {
        trackGA4NumberOfSectionsInEmailCampaign(campaignId);
        void onClickMenuOption(EmailCampaignPublishingOptions.WEBPAGE, campaignId);
      },
      customThumbnailClasses: 'bg-primary-10 content-primary',
    },
  ];

  if (!isCampaignAlreadyPublished) {
    publishingOptions.push({
      id: PublishingOptionsList.EMAIL_CAMPAIGN,
      iconName: 'icon-envelope',
      heading: window.i18next.t('pmwjs_email_campaign_new'),
      subheading: window.i18next.t('pmwjs_email_campaign_publish'),
      thumbNailType: ThumbNailType.ICON,
      onClick: (): void => {
        trackGA4NumberOfSectionsInEmailCampaign(campaignId);
        void onClickMenuOption(EmailCampaignPublishingOptions.EMAIL, campaignId);
      },
      customThumbnailClasses: 'bg-primary-10 content-primary',
    });
  }

  return publishingOptions;
};
